import { useCallback, useState } from 'react';
import box from '~/assets/icons/box_3.svg';
import calendar from '~/assets/icons/calendar.svg';
import cash from '~/assets/icons/cash.svg';
import transfer from '~/assets/icons/transfer.svg';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { Currency, ModalButtonColor, ModalType, WalletType } from '~/shared';
import { useModal } from '../../hooks';
import { AppMyBusinessStatItem } from '../AppMyBusinessStatItem';
import {
	StyledButtonsGrid,
	StyledContainer,
	StyledHeadBgImage,
	StyledHeadBottomContainer,
	StyledHeadContainer,
	StyledHeadPriceContainer,
	StyledHeadTopContainer,
	StyledInfoGrid,
} from './AppMyBusinessAbout.styled';

const businessName = 'Заправка LTD №14';
const accountBalance = 250_000;
const cashBalance = 1_500_000;
const taxPercent = 1.5;

const AppMyBusinessAbout = () => {
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const { showModal } = useModal();
	const [revenueSelected, setRevenueSelected] = useState<
		'day' | 'month' | 'all'
	>('day');
	const [salesSelected, setSalesSelected] = useState<'day' | 'month' | 'all'>(
		'day',
	);
	const [averageCheckSelected, setAverageCheckSelected] = useState<
		'day' | 'month' | 'all'
	>('day');

	const handleWithdrawFromAccount = useCallback(() => {
		showModal(
			{
				type: ModalType.BusinessWithdraw,
				title: 'Забрать средства',
				text: `Введите в поле ниже сумму, которую вы хотите вывести со счёта вашего бизнеса "${businessName}"`,
				balanceLabel: 'Денег на счету',
				balance: accountBalance,
				taxPercent,
				wallet: WalletType.Bank,
				acceptButton: {
					color: ModalButtonColor.Success,
					text: 'Вывести деньги',
				},
				cancelButton: { color: ModalButtonColor.Secondary, text: 'Отменить' },
			},
			(result) => {
				if (!result.isAccepted) {
					return;
				}

				// @TODO: запрос на вывод средств со счёта
				console.log('my-business: withdraw from account', result.value);
			},
		);
	}, [showModal]);

	const handleWithdrawFromCash = useCallback(() => {
		showModal(
			{
				type: ModalType.BusinessWithdraw,
				title: 'Снятие средств',
				text: `Введите в поле ниже сумму, которую вы хотите взять из кассы вашего бизнеса "${businessName}"`,
				balanceLabel: 'Денег в кассе',
				balance: cashBalance,
				taxPercent,
				wallet: WalletType.Cash,
				acceptButton: {
					color: ModalButtonColor.Success,
					text: 'Забрать деньги',
				},
				cancelButton: { color: ModalButtonColor.Secondary, text: 'Отменить' },
			},
			(result) => {
				if (!result.isAccepted) {
					return;
				}

				// @TODO: запрос на снятие средств из кассы
				console.log('my-business: withdraw from cash', result.value);
			},
		);
	}, [showModal]);

	return (
		<StyledContainer>
			<StyledHeadContainer>
				<StyledHeadBgImage />
				<StyledHeadTopContainer>
					<Text style={{ fontSize: 14, color: '#FFFFFF', opacity: 0.5 }}>
						Название бизнеса
					</Text>
					<Text style={{ fontSize: 20, fontWeight: 500, color: '#FFFFFF' }}>
						{businessName}
					</Text>
				</StyledHeadTopContainer>
				<StyledHeadBottomContainer>
					<Text style={{ fontSize: 14, color: '#FFFFFF', opacity: 0.5 }}>
						Гос. стоимость
					</Text>
					<StyledHeadPriceContainer>
						<CurrencyIcon icon={rubleIcon} size="md" />
						<Text>500 000 000</Text>
					</StyledHeadPriceContainer>
				</StyledHeadBottomContainer>
			</StyledHeadContainer>
			<StyledInfoGrid>
				<AppMyBusinessStatItem
					title="Баланс счёта"
					valueIcon={
						<CurrencyIcon icon={rubleIcon} size="lg" asMask color="#3491E9" />
					}
					valueText={accountBalance.toLocaleString('ru-RU')}
				>
					<Button
						color={ButtonColors.LightBlue}
						height={ButtonHeight.Height40}
						borderRadius={ButtonBorderRadius.Radius5}
						style={{ display: 'flex', alignItems: 'center', gap: 10 }}
						onClick={handleWithdrawFromAccount}
					>
						<img src={transfer} width={16} height={16} />
						<Text>Вывести средства</Text>
					</Button>
				</AppMyBusinessStatItem>
				<AppMyBusinessStatItem
					title="Баланс кассы"
					valueIcon={
						<CurrencyIcon icon={rubleIcon} size="lg" asMask color="#3491E9" />
					}
					valueText={cashBalance.toLocaleString('ru-RU')}
				>
					<Button
						color={ButtonColors.LightBlue}
						height={ButtonHeight.Height40}
						borderRadius={ButtonBorderRadius.Radius5}
						style={{ display: 'flex', alignItems: 'center', gap: 10 }}
						onClick={handleWithdrawFromCash}
					>
						<img src={cash} width={16} height={16} />
						<Text>Забрать средства</Text>
					</Button>
				</AppMyBusinessStatItem>
				<AppMyBusinessStatItem
					title="Оплачен на"
					valueIcon={
						<ImgIcon icon={calendar} width={20} height={20} color="#3491E9" />
					}
					valueText="1 500 000"
				>
					<Text style={{ fontSize: 14, color: '#FFFFFF', opacity: 0.25 }}>
						Оплатить налоги вы можете через банк или приложение
					</Text>
				</AppMyBusinessStatItem>
				<AppMyBusinessStatItem
					title="Выручка"
					valueIcon={
						<CurrencyIcon icon={rubleIcon} size="lg" asMask color="#3491E9" />
					}
					valueText="250 000"
				>
					<StyledButtonsGrid>
						<Button
							color={
								revenueSelected === 'day'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setRevenueSelected('day')}
						>
							<Text>День</Text>
						</Button>
						<Button
							color={
								revenueSelected === 'month'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setRevenueSelected('month')}
						>
							<Text>Мес.</Text>
						</Button>
						<Button
							color={
								revenueSelected === 'all'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setRevenueSelected('all')}
						>
							<Text>Все</Text>
						</Button>
					</StyledButtonsGrid>
				</AppMyBusinessStatItem>
				<AppMyBusinessStatItem
					title="Продажи"
					valueIcon={
						<ImgIcon icon={box} width={20} height={20} color="#3491E9" />
					}
					valueText="480"
				>
					<StyledButtonsGrid>
						<Button
							color={
								salesSelected === 'day'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setSalesSelected('day')}
						>
							<Text>День</Text>
						</Button>
						<Button
							color={
								salesSelected === 'month'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setSalesSelected('month')}
						>
							<Text>Мес.</Text>
						</Button>
						<Button
							color={
								salesSelected === 'all'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setSalesSelected('all')}
						>
							<Text>Все</Text>
						</Button>
					</StyledButtonsGrid>
				</AppMyBusinessStatItem>
				<AppMyBusinessStatItem
					title="Средний чек"
					valueIcon={
						<CurrencyIcon icon={rubleIcon} size="lg" asMask color="#3491E9" />
					}
					valueText="4 724"
				>
					<StyledButtonsGrid>
						<Button
							color={
								averageCheckSelected === 'day'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setAverageCheckSelected('day')}
						>
							<Text>День</Text>
						</Button>
						<Button
							color={
								averageCheckSelected === 'month'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setAverageCheckSelected('month')}
						>
							<Text>Мес.</Text>
						</Button>
						<Button
							color={
								averageCheckSelected === 'all'
									? ButtonColors.LightBlue
									: ButtonColors.Default
							}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => setAverageCheckSelected('all')}
						>
							<Text>Все</Text>
						</Button>
					</StyledButtonsGrid>
				</AppMyBusinessStatItem>
			</StyledInfoGrid>
		</StyledContainer>
	);
};

export { AppMyBusinessAbout };
